/** @jsx jsx */
import * as React from 'react';
import { css as compiledCss, jsx } from '@compiled/react';
import { AutoScaledImage } from '@helpcenter/auto-scaled-image';
import AkHeading, { type HeadingProps } from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';

import type { ContainerPadding } from './styled';
import { StyledIconCardLayout, Description, HeadingContent } from './styled';

export interface Props {
	heading?: React.ReactNode;
	description: React.ReactNode;
	/**
	 * Will clamp the heading to 1 line when lineClamp={1} or greater.
	 * Will clamp description to the number of lines specified.
	 * 0 or undefined = no clamping.
	 * 1 = 1 line.
	 * 2 = 2 lines. etc.
	 */
	lineClamp?: number;
	className?: string;
	dataTestId?: string;
	icon?: string | React.ReactNode;
	/**
	 * Used to switch the style of padding inside the container.
	 * "default" is 3 grid lengths, "tight" is 2 grid lengths.
	 * This only affects left/right padding.
	 */
	containerPadding?: ContainerPadding;
	editRequestTypeOnRequestList?: React.ReactElement;
	/**
	 * Specifies the HTML element type to use for the heading.
	 * Defaults to feature flag-controlled behavior if not specified.
	 */
	headingAs?: HeadingProps['as'] | undefined;
	isAriaHidden?: boolean;
}

const GroupBox: React.FunctionComponent<Props> = ({
	heading,
	description,
	icon,
	editRequestTypeOnRequestList,
	headingAs = 'h3',
	isAriaHidden = false,
	lineClamp = 0,
	...props
}) => {
	const showEditRequestButton = !!editRequestTypeOnRequestList;
	const ariaHidden = isAriaHidden ? undefined : true;

	const content = showEditRequestButton ? (
		<div css={GroupBoxWithAdditionalButtonLayout}>
			{heading && (
				<AkHeading size="xsmall" as={headingAs}>
					<HeadingContent lineClamp={!!lineClamp}>{heading}</HeadingContent>
				</AkHeading>
			)}
			<Description aria-hidden={ariaHidden} lineClamp={lineClamp}>
				{description}
			</Description>
			<div css={OverlayButtonLayout} data-edit-overlay="true">
				{editRequestTypeOnRequestList}
			</div>
		</div>
	) : (
		<React.Fragment>
			{heading && (
				<AkHeading size="xsmall" as={headingAs}>
					<HeadingContent lineClamp={!!lineClamp}>{heading}</HeadingContent>
				</AkHeading>
			)}
			<Description aria-hidden={ariaHidden} lineClamp={lineClamp}>
				{description}
			</Description>
		</React.Fragment>
	);

	return (
		<StyledIconCardLayout
			{...props}
			leftIcon={
				icon &&
				(React.isValidElement(icon) ? icon : <AutoScaledImage src={icon as string} alt="" />)
			}
			content={content}
		/>
	);
};

const GroupBoxWithAdditionalButtonLayout = compiledCss({
	position: 'relative',
});

const OverlayButtonLayout = compiledCss({
	position: 'absolute',
	right: token('space.0', '0px'),
	top: token('space.0', '0px'),
	opacity: 0,
});

export default GroupBox;
